/**
 * RichMessageInput -- top-level orchestrator for rich chat input
 *
 * Composes RichTextarea + MentionAutocomplete + CommandPalette.
 * Supports @ entity mentions and / slash commands.
 *
 * Props:
 * - variant='full'    -- Polix AI page (helper text, full controls)
 * - variant='compact' -- Launchpad (minimal chrome)
 */

import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Send, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  RichTextarea,
  type RichTextareaHandle,
} from '@/components/polix-ai/rich-textarea'
import { MentionAutocomplete } from '@/components/polix-ai/mention-autocomplete'
import { CommandPalette } from '@/components/polix-ai/command-palette'
import { useEntitySearch } from '@/hooks/use-entity-search'
import { filterCommands, COMMAND_SYSTEM_PROMPTS } from '@/lib/slash-commands'
import {
  serializeSegments,
  getPlainText,
} from '@/lib/rich-input-serializer'
import { getCaretCoordinates } from '@/lib/caret-utils'
import type {
  InputSegment,
  MentionEntity,
  SlashCommand,
  AutocompleteState,
  RichMessagePayload,
} from '@/types/rich-input'
import { AUTOCOMPLETE_INITIAL } from '@/types/rich-input'

const MESSAGE_MAX_LENGTH = 4000

export interface RichMessageInputProps {
  readonly onSubmit?: (content: string) => void
  readonly onSubmitRich?: (payload: RichMessagePayload, systemPrompt?: string) => void
  readonly onValueChange?: (text: string) => void
  readonly disabled?: boolean
  readonly placeholder?: string
  readonly variant?: 'full' | 'compact'
}

export interface RichMessageInputHandle {
  submit: () => void
  setText: (content: string) => void
}

export const RichMessageInput = forwardRef<RichMessageInputHandle, RichMessageInputProps>(function RichMessageInput({
  onSubmit,
  onSubmitRich,
  onValueChange,
  disabled = false,
  placeholder = 'Type your message...',
  variant = 'full',
}, ref) {
  // -- State --
  const [segments, setSegments] = useState<readonly InputSegment[]>([])
  const [rawText, setRawText] = useState('')
  const [autocomplete, setAutocomplete] =
    useState<AutocompleteState>(AUTOCOMPLETE_INITIAL)
  const [caretRect, setCaretRect] = useState<{
    top: number
    left: number
  } | null>(null)
  const [validationError, setValidationError] = useState<string | undefined>()
  const [activeCommand, setActiveCommand] = useState<SlashCommand | undefined>()

  const richTextareaRef = useRef<RichTextareaHandle>(null)

  // -- Derived --
  const plainText = getPlainText(segments)
  const charCount = plainText.trim().length
  const showCharCount = charCount > MESSAGE_MAX_LENGTH * 0.8
  const canSend = !disabled && charCount > 0 && charCount <= MESSAGE_MAX_LENGTH

  // -- Entity search --
  const entitySearch = useEntitySearch({
    query: autocomplete.query,
    enabled: autocomplete.mode === 'mention',
  })

  // -- Command filter --
  const filteredCommands =
    autocomplete.mode === 'command' ? filterCommands(autocomplete.query) : []

  // Total items for keyboard nav
  const totalItems =
    autocomplete.mode === 'mention'
      ? entitySearch.flatResults.length
      : autocomplete.mode === 'command'
        ? filteredCommands.length
        : 0

  // -- Validation --
  useEffect(() => {
    if (charCount > MESSAGE_MAX_LENGTH) {
      setValidationError(
        `Message is too long (${charCount}/${MESSAGE_MAX_LENGTH} characters)`
      )
    } else {
      setValidationError(undefined)
    }
  }, [charCount])

  // -- Notify parent of value changes --
  useEffect(() => {
    onValueChange?.(rawText)
  }, [rawText, onValueChange])

  // -- Auto-focus on mount (desktop only) --
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    if (!isMobile && !disabled) {
      const timer = setTimeout(() => richTextareaRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [disabled])

  // -- Helpers --

  const updateCaretRect = useCallback(() => {
    const el = richTextareaRef.current?.getTextareaElement()
    if (!el) return

    const pos = el.selectionStart
    const coords = getCaretCoordinates(el, pos)

    setCaretRect({
      top: coords.top + coords.height + 4,
      left: Math.min(coords.left, el.clientWidth - 320),
    })
  }, [])

  const closeAutocomplete = useCallback(() => {
    setAutocomplete(AUTOCOMPLETE_INITIAL)
    setCaretRect(null)
  }, [])

  // -- Text change handler --

  const handleTextChange = useCallback(
    (text: string) => {
      setRawText(text)

      // Rebuild segments from raw text, preserving existing mentions
      // For simplicity, when the user edits text, we re-derive segments
      // Mention tokens are tracked by their position in rawText as
      // `@[DisplayName]` -- if the user edits inside one, it becomes text.
      const newSegments: InputSegment[] = [{ type: 'text', text }]
      setSegments(newSegments)

      // Detect @ or / trigger
      const el = richTextareaRef.current?.getTextareaElement()
      if (!el) return

      const cursorPos = el.selectionStart
      const textBefore = text.substring(0, cursorPos)

      // Check for @ trigger (not preceded by word char)
      const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_. ]*)$/)
      if (atMatch) {
        const query = atMatch[2]
        const triggerStart = textBefore.lastIndexOf('@')
        setAutocomplete({
          mode: 'mention',
          query,
          cursorIndex: cursorPos,
          selectedIndex: 0,
          triggerStartIndex: triggerStart,
        })
        updateCaretRect()
        return
      }

      // Check for / at line start
      const slashMatch = textBefore.match(/(^|\n)\/([a-zA-Z0-9-]*)$/)
      if (slashMatch) {
        const query = slashMatch[2]
        const triggerStart = textBefore.lastIndexOf('/')
        setAutocomplete({
          mode: 'command',
          query,
          cursorIndex: cursorPos,
          selectedIndex: 0,
          triggerStartIndex: triggerStart,
        })
        updateCaretRect()
        return
      }

      // No trigger active
      if (autocomplete.mode !== 'none') {
        closeAutocomplete()
      }
    },
    [autocomplete.mode, closeAutocomplete, updateCaretRect]
  )

  // -- Selection handler (for re-checking triggers on cursor move) --

  const handleSelect = useCallback(() => {
    const el = richTextareaRef.current?.getTextareaElement()
    if (!el || autocomplete.mode === 'none') return

    const cursorPos = el.selectionStart
    const textBefore = rawText.substring(0, cursorPos)

    // If cursor moved outside the trigger range, close
    if (autocomplete.mode === 'mention') {
      const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_. ]*)$/)
      if (!atMatch) {
        closeAutocomplete()
      }
    } else if (autocomplete.mode === 'command') {
      const slashMatch = textBefore.match(/(^|\n)\/([a-zA-Z0-9-]*)$/)
      if (!slashMatch) {
        closeAutocomplete()
      }
    }
  }, [autocomplete.mode, rawText, closeAutocomplete])

  // -- Mention selection --

  const handleMentionSelect = useCallback(
    (entity: MentionEntity) => {
      const { triggerStartIndex, cursorIndex } = autocomplete
      const before = rawText.substring(0, triggerStartIndex)
      const after = rawText.substring(cursorIndex)
      const mentionText = `@${entity.displayName} `
      const newText = before + mentionText + after

      // Build segments with the mention
      const newSegments: InputSegment[] = []
      if (before) {
        newSegments.push({ type: 'text', text: before })
      }
      newSegments.push({ type: 'mention', entity })
      if (after || true) {
        newSegments.push({ type: 'text', text: ' ' + after })
      }

      setRawText(newText)
      setSegments(newSegments)
      closeAutocomplete()

      // Restore focus and cursor position
      requestAnimationFrame(() => {
        const el = richTextareaRef.current?.getTextareaElement()
        if (el) {
          const newPos = before.length + mentionText.length
          el.focus()
          el.setSelectionRange(newPos, newPos)
        }
      })
    },
    [autocomplete, rawText, closeAutocomplete]
  )

  // -- Command selection --

  const handleCommandSelect = useCallback(
    (command: SlashCommand) => {
      const { triggerStartIndex, cursorIndex } = autocomplete
      const before = rawText.substring(0, triggerStartIndex)
      const after = rawText.substring(cursorIndex)
      const commandText = `/${command.name} `
      const newText = before + commandText + after

      setRawText(newText)
      setSegments([{ type: 'text', text: newText }])
      setActiveCommand(command)
      closeAutocomplete()

      requestAnimationFrame(() => {
        const el = richTextareaRef.current?.getTextareaElement()
        if (el) {
          const newPos = before.length + commandText.length
          el.focus()
          el.setSelectionRange(newPos, newPos)
        }
      })
    },
    [autocomplete, rawText, closeAutocomplete]
  )

  // -- Mention removal --

  const handleMentionRemove = useCallback(
    (entity: MentionEntity) => {
      const newSegments = segments.filter(
        (s) => !(s.type === 'mention' && s.entity.id === entity.id)
      )
      setSegments(newSegments)
      // Rebuild rawText without the mention
      const newText = newSegments
        .map((s) => (s.type === 'text' ? s.text : `@${s.entity.displayName}`))
        .join('')
      setRawText(newText)
    },
    [segments]
  )

  // -- Submit --

  const handleSubmit = useCallback(() => {
    if (!canSend) return

    const payload = serializeSegments(segments, activeCommand?.name)
    const systemPrompt = activeCommand
      ? COMMAND_SYSTEM_PROMPTS[activeCommand.id]
      : undefined

    if (onSubmitRich) {
      onSubmitRich(payload, systemPrompt)
    } else if (onSubmit) {
      onSubmit(payload.content)
    }

    // Reset
    setRawText('')
    setSegments([])
    setActiveCommand(undefined)
    closeAutocomplete()
    setValidationError(undefined)

    // Refocus
    requestAnimationFrame(() => richTextareaRef.current?.focus())
  }, [
    canSend,
    segments,
    activeCommand,
    onSubmitRich,
    onSubmit,
    closeAutocomplete,
  ])

  const setText = useCallback((content: string) => {
    const value = content.trim()
    setRawText(value)
    setSegments(value ? [{ type: 'text', text: value }] : [])
    setActiveCommand(undefined)
    closeAutocomplete()
    setValidationError(undefined)

    requestAnimationFrame(() => {
      const el = richTextareaRef.current?.getTextareaElement()
      if (!el) return
      el.focus()
      const endPos = value.length
      el.setSelectionRange(endPos, endPos)
    })
  }, [closeAutocomplete])

  // -- Expose submit to parent via ref --
  useImperativeHandle(ref, () => ({ submit: handleSubmit, setText }), [handleSubmit, setText])

  // -- Keyboard handler --

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // When autocomplete is open, intercept navigation keys
      if (autocomplete.mode !== 'none' && totalItems > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setAutocomplete((prev) => ({
            ...prev,
            selectedIndex: (prev.selectedIndex + 1) % totalItems,
          }))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setAutocomplete((prev) => ({
            ...prev,
            selectedIndex:
              (prev.selectedIndex - 1 + totalItems) % totalItems,
          }))
          return
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          if (autocomplete.mode === 'mention') {
            const entity = entitySearch.flatResults[autocomplete.selectedIndex]
            if (entity) handleMentionSelect(entity)
          } else if (autocomplete.mode === 'command') {
            const cmd = filteredCommands[autocomplete.selectedIndex]
            if (cmd) handleCommandSelect(cmd)
          }
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          closeAutocomplete()
          return
        }
      }

      // Enter sends (Shift+Enter for newline)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
        return
      }

      // Escape clears input
      if (e.key === 'Escape') {
        e.preventDefault()
        setRawText('')
        setSegments([])
        setActiveCommand(undefined)
        setValidationError(undefined)
      }
    },
    [
      autocomplete,
      totalItems,
      entitySearch.flatResults,
      filteredCommands,
      handleMentionSelect,
      handleCommandSelect,
      closeAutocomplete,
      handleSubmit,
    ]
  )

  // ARIA attributes for combobox pattern
  const ariaProps: Record<string, string | boolean | undefined> = {
    role: 'combobox',
    'aria-expanded': autocomplete.mode !== 'none',
    'aria-haspopup': 'listbox',
    'aria-activedescendant':
      autocomplete.mode === 'mention'
        ? `mention-option-${autocomplete.selectedIndex}`
        : autocomplete.mode === 'command'
          ? `command-option-${autocomplete.selectedIndex}`
          : undefined,
  }

  return (
    <div
      className={cn(
        variant === 'full' && 'border-t border-border bg-background p-4',
        variant === 'compact' && ''
      )}
      {...ariaProps}
    >
      <div className="mx-auto">
        <div
          className={cn(
            'flex items-end gap-2',
            variant === 'compact' && 'gap-0'
          )}
        >
          {/* Rich textarea with overlay */}
          <div className="relative flex-1 space-y-1">
            <div
              className={cn(
                'rounded-lg border transition-colors',
                'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                validationError && 'border-destructive',
                disabled && 'opacity-50'
              )}
            >
              <RichTextarea
                ref={richTextareaRef}
                segments={segments}
                rawText={rawText}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                onSelect={handleSelect}
                disabled={disabled}
                placeholder={placeholder}
                onMentionRemove={handleMentionRemove}
              />

              {/* Mention autocomplete popover */}
              {autocomplete.mode === 'mention' && caretRect && (
                <MentionAutocomplete
                  results={entitySearch.results}
                  flatResults={entitySearch.flatResults}
                  isLoading={entitySearch.isLoading}
                  isEmpty={entitySearch.isEmpty}
                  query={autocomplete.query}
                  selectedIndex={autocomplete.selectedIndex}
                  anchorRect={caretRect}
                  onSelect={handleMentionSelect}
                />
              )}

              {/* Command palette popover */}
              {autocomplete.mode === 'command' && caretRect && (
                <CommandPalette
                  commands={filteredCommands}
                  selectedIndex={autocomplete.selectedIndex}
                  anchorRect={caretRect}
                  onSelect={handleCommandSelect}
                />
              )}
            </div>

            {/* Validation error */}
            {validationError && (
              <div
                className="flex items-center gap-1 text-xs text-destructive"
                role="alert"
              >
                <AlertCircle className="h-3 w-3" />
                <span>{validationError}</span>
              </div>
            )}
          </div>

          {/* Send button */}
          {variant === 'full' && (
            <Button
              onClick={handleSubmit}
              disabled={!canSend}
              size="icon"
              className={cn(
                'h-[60px] w-[60px] shrink-0 rounded-lg',
                'transition-[border-color,box-shadow] duration-200',
                canSend
                  ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              )}
              aria-label="Send message"
            >
              {disabled ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          )}
        </div>

        {/* Helper text & character count */}
        {variant === 'full' && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Press{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">
                Enter
              </kbd>{' '}
              to send,{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">
                Shift+Enter
              </kbd>{' '}
              for new line &middot; Type{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">@</kbd>{' '}
              to mention &middot;{' '}
              <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">/</kbd>{' '}
              for commands
            </span>
            <div className="flex items-center gap-3">
              {showCharCount && (
                <span
                  className={cn(
                    'font-mono',
                    charCount > MESSAGE_MAX_LENGTH &&
                      'text-destructive font-semibold'
                  )}
                >
                  {charCount}/{MESSAGE_MAX_LENGTH}
                </span>
              )}
              {disabled && <span className="text-primary">Sending...</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
